import { useEffect, useState } from 'react'
import { trackEvent } from '../../analytics'
import {
  SectionTitle,
  SectionTitleSize,
  SectionTitleStyle
} from '../SectionTitle/SectionTitle'

import { ContractorsResult } from '../ContractorsResult/ContractorsResult'
import { Select } from '../Select/Select'
import { NumberField } from '../NumberField/NumberField'

import styles from './MultiForm.module.css'
import { RouteSelection } from '../../wizard/RouteSelection/RouteSelection'
import { InputField } from '../InputField/InputField'
import { Text } from '../Text/Text'
import useFetch from 'use-http'
import { API_URL } from '../../util'
import { useAppContext } from '../../Context'
import { Link } from 'react-router-dom'
import { routes } from '../../routes'
import { Button } from 'src/wizard/Button/Button'
import { Captcha } from '../Captcha/Captcha'
import { Notification } from '../Notification/Notification'
import { StepProgress } from '../../wizard/StepProgress/StepProgress'
import { getStepsForRoute, getStepIndex, Steps } from './wizardSteps'
import { defaultWizard } from '../../App'
import { WizardSpinner } from '../../wizard/WizardSpinner/WizardSpinner'

type WizardRoute = 'contractor' | 'benchmark'
type WizardData = {
  route: WizardRoute | null
  contractor: string | null
  service: string | null
  projectName: string
  frameworkType: 'ep-rates' | 'sor' | 'quantity' | null
  specification: { [field: string]: string }
  details: {
    budget: number | ''
    quantity: number | ''
    location: string
  }
  historical: {
    cost: number | ''
    procurement: string | null
    framework: string | null
    contractor: string | null
    performance: number | null
  }
  sorType: string
  sorAdjustment: number | ''
  sorBudget: number | ''
  quantityInputs: { [choiceId: string]: number | '' }
  quantityBudget: number | ''
  quantityProcurement: string
}

function nextStep(step: Steps, route: WizardRoute, frameworkType: string | null) {
  if (route === 'benchmark' && frameworkType === 'sor') {
    switch (step) {
      case Steps.Route: return Steps.Service
      case Steps.Service: return Steps.SorInput
      case Steps.SorInput: return Steps.Results
    }
    return Steps.Route
  }
  if (route === 'benchmark' && frameworkType === 'quantity') {
    switch (step) {
      case Steps.Route: return Steps.Service
      case Steps.Service: return Steps.QuantityInput
      case Steps.QuantityInput: return Steps.Results
    }
    return Steps.Route
  }
  switch (step) {
    case Steps.Route: return route === 'contractor' ? Steps.Contractor : Steps.Service
    case Steps.Contractor: return Steps.Service
    case Steps.Service: return Steps.Specification
    case Steps.Specification: return Steps.Details
    // Historical (captcha) step removed from the EP Rates flow (#19)
    case Steps.Details: return Steps.Results
  }
  return Steps.Route
}

function prevStep(step: Steps, route: WizardRoute, frameworkType: string | null) {
  if (route === 'benchmark' && frameworkType === 'sor') {
    switch (step) {
      case Steps.SorInput: return Steps.Service
      case Steps.Results: return Steps.SorInput
    }
    return Steps.Route
  }
  if (route === 'benchmark' && frameworkType === 'quantity') {
    switch (step) {
      case Steps.QuantityInput: return Steps.Service
      case Steps.Results: return Steps.QuantityInput
    }
    return Steps.Route
  }
  switch (step) {
    case Steps.Contractor: return Steps.Route
    case Steps.Service: return route === 'contractor' ? Steps.Contractor : Steps.Route
    case Steps.Specification: return Steps.Service
    case Steps.Details: return Steps.Specification
    // Historical (captcha) step removed from the EP Rates flow (#19)
    case Steps.Results: return Steps.Details
  }
  return Steps.Route
}

type FormProps = {
  step: Steps
  other: () => void
  setCaptchaToken: React.Dispatch<React.SetStateAction<string>>
  setGrecaptcha: React.Dispatch<
    React.SetStateAction<ReCaptchaV2.ReCaptcha | undefined>
  >
  contractors: Contractor[] | undefined
  frameworks: any[] | undefined
  choices: any[] | undefined
}

type Contractor = {
  _id: string
  name: string
  frameworks: { contractorFramework?: { frameworkId: string } }[]
}

function FormSteps(props: FormProps) {
  const context = useAppContext()
  const { wizard: form, setContext } = context
  const setForm = (s: WizardData) => setContext({ ...context, wizard: s })

  const { contractors, frameworks, choices } = props

  const contractor =
    form.contractor && contractors
      ? contractors.find((c: Contractor) => c._id === form.contractor)
      : null

  const onHistoricalChange = (field: string, value: string | number) => {
    setForm({ ...form, historical: { ...form.historical, [field]: value } })
  }

  if (props.step !== Steps.Route && (!contractors || !frameworks || !choices))
    return <WizardSpinner />

  if (props.step === Steps.Contractor)
    return (
      <div className={styles.fullWidth}>
        <div className={styles.grid}>
          <SectionTitle
            title={'SELECT CONTRACTOR'}
            size={SectionTitleSize.Small}
            style={SectionTitleStyle.LightBlue}
          />
          <Select
            options={[
              { value: '', label: '', disabled: true },
              ...((contractors as Contractor[]) ?? [])
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((c) => ({ value: c._id, label: c.name }))
            ]}
            value={form.contractor ?? ''}
            onChange={(e) => setForm({ ...form, contractor: e.target.value })}
            required={true}
          />
        </div>
      </div>
    )

  if (props.step === Steps.Service)
    return (
      <div className={styles.fullWidth}>
        <div className={styles.grid}>
          <SectionTitle
            title={'SELECT SERVICE'}
            size={SectionTitleSize.Small}
            style={SectionTitleStyle.LightBlue}
          />
          <Select
            options={[
              { value: '', label: '', disabled: true },
              ...(frameworks ?? [])
                .filter((f: { _id: string; type?: string }) => {
                  if (!contractor) return true
                  return contractor.frameworks.find(
                    (cf: { contractorFramework?: { frameworkId: string } }) =>
                      cf.contractorFramework?.frameworkId === f._id
                  )
                })
                .map((f: { _id: string; name: string }) => ({
                  value: f._id,
                  label: f.name
                }))
            ]}
            value={form.service ?? ''}
            onChange={(e) => {
              const selectedFramework = (frameworks ?? []).find(
                (f: { _id: string }) => f._id === e.target.value
              )
              setForm({
                ...form,
                service: e.target.value,
                frameworkType: selectedFramework?.type ?? 'ep-rates',
                quantityInputs: {}
              })
            }}
            required={true}
          />
          {form.route === 'contractor' ? (
            <Text>
              Can't find the service you're looking for?{' '}
              <span onClick={props.other} className={styles.look}>
                Look for all contractors.
              </span>
            </Text>
          ) : null}
        </div>
      </div>
    )

  if (props.step === Steps.Specification) {
    const questions = (choices ?? []).filter(
      (c: { frameworkId: string; isCore: boolean }) =>
        c.frameworkId === form.service && !c.isCore
    )
    return (
      <div className={styles.fullWidth}>
        <div className={styles.grid}>
          <SectionTitle
            title={'SELECT SPECIFICATION AND OPTIONS'}
            size={SectionTitleSize.Small}
            style={SectionTitleStyle.LightBlue}
          />
          {questions.map(
            (q: { _id: string; name: string; options: { name: string }[] }) => {
              return (
                <label key={q._id}>
                  <Text>{q.name}</Text>
                  <Select
                    options={[
                      { value: '', label: '', disabled: true },
                      ...q.options.map((o: { name: string }) => ({
                        value: o.name,
                        label: o.name
                      }))
                    ]}
                    value={form.specification[q._id] ?? ''}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        specification: {
                          ...form.specification,
                          [q._id]: e.target.value
                        }
                      })
                    }
                    required={true}
                  />
                </label>
              )
            }
          )}
        </div>
      </div>
    )
  }

  if (props.step === Steps.Details)
    return (
      <div className={styles.fullWidth}>
        <div className={styles.grid}>
          <SectionTitle
            title={'ENTER PROJECT DETAILS'}
            size={SectionTitleSize.Small}
            style={SectionTitleStyle.LightBlue}
          />
          <label>
            <Text>Project name</Text>
            <InputField
              type={'text'}
              value={form.projectName}
              onChange={(event) =>
                setForm({ ...form, projectName: event.target.value })
              }
            />
          </label>
          <label>
            <Text>Project Budget</Text>
            <NumberField
              min={1}
              placeholder={'1'}
              value={form.details.budget}
              onChange={(event) =>
                setForm({
                  ...form,
                  details: {
                    ...form.details,
                    budget: event.target.value ? Number(event.target.value) : ''
                  }
                })
              }
              step={1}
              required={true}
            />
          </label>
          <label>
            <Text>Quantity</Text>
            <NumberField
              value={form.details.quantity}
              onChange={(event) =>
                setForm({
                  ...form,
                  details: {
                    ...form.details,
                    quantity: event.target.value
                      ? Number(event.target.value)
                      : ''
                  }
                })
              }
              min={1}
              step={1}
              required={true}
            />
          </label>
          {/* Location field removed from the EP Rates flow (#19) */}
        </div>
      </div>
    )

  if (props.step === Steps.Historical)
    return (
      <div className={styles.fullWidth}>
        <div className={styles.grid}>
          <SectionTitle
            title={'ENTER HISTORICAL DETAILS'}
            size={SectionTitleSize.Small}
            style={SectionTitleStyle.LightBlue}
          />
          <label>
            <Text>Average cost per item including VAT</Text>
            <NumberField
              value={form.historical.cost}
              step={1}
              min={0}
              onChange={(event) =>
                onHistoricalChange(
                  'cost',
                  event.target.value ? Number(event.target.value) : ''
                )
              }
            />
          </label>
          <label>
            <Text>How did you procure?</Text>
            <Select
              options={[
                { value: '', label: '', disabled: true },
                { value: 'framework', label: 'Framework' },
                { value: 'self', label: 'Self procured' },
                { value: 'negotiated', label: 'Negotiated' }
              ]}
              value={form.historical.procurement ?? ''}
              onChange={(event) =>
                onHistoricalChange('procurement', event.target.value)
              }
            />
          </label>
          {form.historical.procurement === 'framework' ? (
            <>
              <label>
                <Text>Which framework did you use?</Text>
                <InputField
                  type={'text'}
                  value={form.historical.framework ?? ''}
                  onChange={(event) =>
                    onHistoricalChange('framework', event.target.value)
                  }
                  required={true}
                />
              </label>
              <label>
                <Text>Who was the contractor?</Text>
                <InputField
                  type={'text'}
                  value={form.historical.contractor ?? ''}
                  onChange={(event) =>
                    onHistoricalChange('contractor', event.target.value)
                  }
                  required={true}
                />
              </label>
              <label>
                <Text>Rate their performance</Text>
                <Select
                  options={[
                    { value: '-1', label: '', disabled: true },
                    { value: '1', label: '1 (Poor)' },
                    { value: '2', label: '2' },
                    { value: '3', label: '3' },
                    { value: '4', label: '4' },
                    { value: '5', label: '5' },
                    { value: '6', label: '6' },
                    { value: '7', label: '7' },
                    { value: '8', label: '8' },
                    { value: '9', label: '9' },
                    { value: '10', label: '10 (Excellent)' }
                  ]}
                  value={(form.historical.performance ?? -1).toString()}
                  onChange={(event) =>
                    onHistoricalChange(
                      'performance',
                      Number(event.target.value)
                    )
                  }
                  required={true}
                />
              </label>
            </>
          ) : null}
          <Captcha
            onChange={(token) => props.setCaptchaToken(token)}
            setGrecaptcha={props.setGrecaptcha}
          />
        </div>
      </div>
    )

  if (props.step === Steps.SorInput) {
    const selectedFramework = (frameworks ?? []).find(
      (f: { _id: string }) => f._id === form.service
    )
    const sorType: string = selectedFramework?.sorType ?? ''
    const benchmarkText = selectedFramework?.benchmarkText ?? ''

    return (
      <div className={styles.fullWidth}>
        <div className={styles.grid}>
          <SectionTitle
            title={'SOR BENCHMARK'}
            size={SectionTitleSize.Small}
            style={SectionTitleStyle.LightBlue}
          />
          {benchmarkText ? <Text>{benchmarkText}</Text> : null}
          <label>
            <Text>Project name</Text>
            <InputField
              type={'text'}
              value={form.projectName}
              onChange={(event) =>
                setForm({ ...form, projectName: event.target.value })
              }
            />
          </label>
          <label>
            <Text>Select Your Current Schedule of Rates</Text>
            <Select
              options={[
                { value: '', label: '', disabled: true },
                { value: sorType, label: sorType }
              ]}
              value={form.sorType ?? ''}
              onChange={(e) => setForm({ ...form, sorType: e.target.value })}
              required={true}
            />
          </label>
          <label>
            <Text>Your Current Adjustment (%)</Text>
            <NumberField
              value={form.sorAdjustment}
              step={0.01}
              onChange={(event) =>
                setForm({
                  ...form,
                  sorAdjustment: event.target.value ? Number(event.target.value) : ''
                })
              }
              required={true}
            />
          </label>
          <label>
            <Text>Annual Budget / Expenditure</Text>
            <NumberField
              min={1}
              value={form.sorBudget}
              step={1}
              onChange={(event) =>
                setForm({
                  ...form,
                  sorBudget: event.target.value ? Number(event.target.value) : ''
                })
              }
              required={true}
            />
          </label>
        </div>
      </div>
    )
  }

  if (props.step === Steps.QuantityInput) {
    const selectedFramework = (frameworks ?? []).find(
      (f: { _id: string }) => f._id === form.service
    )
    const benchmarkText = selectedFramework?.benchmarkText ?? ''
    const coreChoiceIds: string[] = selectedFramework?.core?.map(
      (c: { choiceId: string }) => c.choiceId
    ) ?? []
    const coreChoices = coreChoiceIds
      .map((id: string) =>
        (choices ?? []).find((c: { _id: string }) => c._id === id)
      )
      .filter(Boolean)

    return (
      <div className={styles.fullWidth}>
        <div className={styles.grid}>
          <SectionTitle
            title={'QUANTITY BENCHMARK'}
            size={SectionTitleSize.Small}
            style={SectionTitleStyle.LightBlue}
          />
          {benchmarkText ? <Text>{benchmarkText}</Text> : null}
          <label>
            <Text>Project name</Text>
            <InputField
              type={'text'}
              value={form.projectName}
              onChange={(event) =>
                setForm({ ...form, projectName: event.target.value })
              }
            />
          </label>
          {coreChoices.map((choice: { _id: string; name: string }) => (
            <label key={choice._id}>
              <Text>{choice.name}</Text>
              <NumberField
                min={0}
                step={1}
                value={form.quantityInputs[choice._id] ?? ''}
                onChange={(event) =>
                  setForm({
                    ...form,
                    quantityInputs: {
                      ...form.quantityInputs,
                      [choice._id]: event.target.value ? Number(event.target.value) : ''
                    }
                  })
                }
                required={false}
              />
            </label>
          ))}
          <label>
            <Text>Annual Budget / Expenditure</Text>
            <NumberField
              min={1}
              value={form.quantityBudget}
              step={1}
              onChange={(event) =>
                setForm({
                  ...form,
                  quantityBudget: event.target.value ? Number(event.target.value) : ''
                })
              }
              required={true}
            />
          </label>
          <label>
            <Text>How do you currently procure?</Text>
            <Select
              options={[
                { value: '', label: '', disabled: true },
                { value: 'framework', label: 'Framework' },
                { value: 'self', label: 'Self procured' },
                { value: 'negotiated', label: 'Negotiated' }
              ]}
              value={form.quantityProcurement ?? ''}
              onChange={(event) =>
                setForm({ ...form, quantityProcurement: event.target.value })
              }
            />
          </label>
        </div>
      </div>
    )
  }

  return null
}

const MultiForm = () => {
  const [step, setStep] = useState(Steps.Route)
  const [error, setError] = useState<{
    success: boolean
    message: string
  } | null>(null)
  const [captchaToken, setCaptchaToken] = useState('')
  const [grecaptcha, setGrecaptcha] = useState<ReCaptchaV2.ReCaptcha>()

  const [submission, setSubmission] = useState<string | null>(null)
  const [results, setResults] = useState<any[]>([])
  const [resultBudget, setResultBudget] = useState<number | undefined>(undefined)
  const [resultBudgetLabel, setResultBudgetLabel] = useState<string | undefined>(undefined)
  const [resultAmountLabel, setResultAmountLabel] = useState<string | undefined>(undefined)
  const [loading, setLoading] = useState(false)

  const fetchOptions = { headers: { 'Content-Type': 'application/json' } }
  const { data: contractors } = useFetch(`${API_URL}contractors`, fetchOptions, [])
  const { data: frameworks } = useFetch(`${API_URL}frameworks`, fetchOptions, [])
  const { data: choices } = useFetch(`${API_URL}choices`, fetchOptions, [])

  const context = useAppContext()

  const next = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()

    const form = document.querySelector('form')
    for (const field of (form as HTMLFormElement).elements) {
      if ((field as HTMLInputElement).reportValidity() === false) return
    }

    trackEvent('wizard_step', { step: Steps[step] })
    setStep(nextStep(step, context.wizard.route as WizardRoute, context.wizard.frameworkType))
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const prev = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    setError(null)

    setStep(prevStep(step, context.wizard.route as WizardRoute, context.wizard.frameworkType))
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  useEffect(() => {
    // if we're on the wrong step then nothing more to do
    if (step !== Steps.Results) return

    if (context.wizard.frameworkType === 'sor') {
      setResults([])
      setLoading(true)
      ;(async function () {
        try {
          if (context.user === null) return
          setError(null)

          const data = new URLSearchParams()
          data.append('username', context.user.username as string)
          data.append('frameworkId', context.wizard.service as string)
          data.append('sorType', context.wizard.sorType)
          data.append('adjustment', String(context.wizard.sorAdjustment))
          data.append('budget', String(context.wizard.sorBudget))
          data.append('projectName', context.wizard.projectName)

          const response = await fetch(`${API_URL}sor/benchmark`, {
            method: 'post',
            headers: { Authorization: `Token ${context.user.token}` },
            body: data
          })

          if (!response.ok) {
            setError({ success: false, message: 'Request failed. Please go back and try again.' })
            return
          }

          const json = await response.json()
          if (!json.success) {
            setError({ success: false, message: json.message + ' Please go back and try again.' })
            return
          }

          setResults(json.results)
          setSubmission(json.submission)
          setResultBudget(json.budget)
          setResultBudgetLabel('CURRENT SPEND')
          setResultAmountLabel('BENCHMARK RESULT')
          trackEvent('wizard_complete', { route: 'benchmark', frameworkType: 'sor', results: json.results.length })
        } finally {
          setLoading(false)
        }
      })()
      return
    }

    if (context.wizard.frameworkType === 'quantity') {
      setResults([])
      setLoading(true)
      ;(async function () {
        try {
          if (context.user === null) return
          setError(null)

          const data = new URLSearchParams()
          data.append('username', context.user.username as string)
          data.append('frameworkId', context.wizard.service as string)
          data.append('quantities', JSON.stringify(
            Object.fromEntries(
              Object.entries(context.wizard.quantityInputs).map(([k, v]) => [
                k, typeof v === 'number' ? v : 0
              ])
            )
          ))
          data.append('budget', String(context.wizard.quantityBudget || 0))
          data.append('projectName', context.wizard.projectName)

          const response = await fetch(`${API_URL}quantity/benchmark`, {
            method: 'post',
            headers: { Authorization: `Token ${context.user.token}` },
            body: data
          })

          if (!response.ok) {
            setError({ success: false, message: 'Request failed. Please go back and try again.' })
            return
          }

          const json = await response.json()
          if (!json.success) {
            setError({ success: false, message: json.message + ' Please go back and try again.' })
            return
          }

          setResults(json.results)
          setSubmission(json.submission)
          setResultBudget(json.budget)
          setResultBudgetLabel('CURRENT ANNUAL COST')
          setResultAmountLabel('BENCHMARK COST')
          trackEvent('wizard_complete', { route: 'benchmark', frameworkType: 'quantity', results: json.results.length })
        } finally {
          setLoading(false)
        }
      })()
      return
    }

    // clear out any results
    context.setContext({ ...context, results: [] })

    // make the form submission
    setLoading(true)
    ;(async function () {
      try {
        if (context.user === null) return
        setError(null)

        const data = new URLSearchParams()
        data.append('username', context.user.username as string)
        data.append('form', JSON.stringify(context.wizard))
        data.append('captcha', captchaToken)

        const response = await fetch(`${API_URL}wizard/submit`, {
          method: 'post',
          headers: {
            Authorization: `Token ${context.user.token}`
          },
          body: data
        })

        grecaptcha?.reset()

        if (!response.ok) {
          console.error(response.status)
          return
        }

        const json = await response.json()

        if (!json.success) {
          setError({
            success: false,
            message: json.message + ' Please go back and try again.'
          })
          return
        }

        context.setContext({ ...context, results: json.results })
        setSubmission(json.submission)
        trackEvent('wizard_complete', { route: context.wizard.route as string, results: json.results.length })
      } finally {
        setLoading(false)
      }
    })()
  }, [context.wizard, step])

  if (
    context.user === null ||
    context.user?.permission === 'yJsiaQY8KbYNpYH5Q'
  ) {
    return (
      <div className={styles.pageWrapper}>
        <div className={styles.innerPageWrapper}>
          <div className={styles.container}>
            <div className={styles.accessMessage}>
              <div style={{ fontSize: '20px', fontWeight: '500' }}>
                {context.user
                  ? 'Permission denied.'
                  : 'You need to be logged in to use the benchmark wizard.'}
              </div>
              {context.user === null ? (
                <div>
                  <Link className={styles.login} to={routes.login.path}>
                    Login
                  </Link>{' '}
                  or{' '}
                  <Link className={styles.register} to={routes.register.path}>
                    Register
                  </Link>
                </div>
              ) : undefined}
            </div>
          </div>
        </div>
      </div>
    )
  }

  const other = () => {
    context.setContext({
      ...context,
      wizard: { ...context.wizard, route: 'benchmark', contractor: null }
    })
    setStep(nextStep(Steps.Route, 'benchmark', null))
  }

  const notification = error ? <Notification response={error} /> : ''

  const route = context.wizard.route as WizardRoute | null
  const showStepper = step !== Steps.Route && route !== null
  const stepDefs = route ? getStepsForRoute(route, context.wizard.frameworkType) : []
  const currentStepIndex = route ? getStepIndex(route, step, context.wizard.frameworkType) : 0

  const loadingMessage =
    context.wizard.frameworkType === 'sor'
      ? 'Calculating SOR benchmarks...'
      : context.wizard.frameworkType === 'quantity'
      ? 'Calculating quantity costs...'
      : 'Finding contractors...'

  return (
    <div className={styles.pageWrapper}>
      <div className={styles.innerPageWrapper}>
        <div className={styles.container}>
          {showStepper && (
            <StepProgress steps={stepDefs} currentIndex={currentStepIndex} />
          )}
          <form>
            <div className={`${styles.row} ${styles.fadeIn}`} key={step}>
              {step === Steps.Results && loading ? (
                <WizardSpinner message={loadingMessage} />
              ) : step === Steps.Results ? (
                <ContractorsResult
                  results={
                    context.wizard.frameworkType === 'sor' || context.wizard.frameworkType === 'quantity'
                      ? results
                      : context.results
                  }
                  submission={submission}
                  other={other}
                  projectName={context.wizard.projectName}
                  budget={resultBudget}
                  budgetLabel={resultBudgetLabel}
                  amountLabel={resultAmountLabel}
                  frameworkName={
                    (frameworks ?? []).find(
                      (f: { _id: string }) => f._id === context.wizard.service
                    )?.name
                  }
                  framework={(frameworks ?? []).find(
                    (f: { _id: string }) => f._id === context.wizard.service
                  )}
                  choices={choices}
                />
              ) : step === Steps.Route ? (
                <RouteSelection
                  onContractor={() => {
                    context.setContext({
                      ...context,
                      wizard: { ...defaultWizard, route: 'contractor' }
                    })
                    setStep(nextStep(step, 'contractor', null))
                  }}
                  onBenchmark={() => {
                    context.setContext({
                      ...context,
                      wizard: { ...defaultWizard, route: 'benchmark' }
                    })
                    setStep(nextStep(step, 'benchmark', null))
                  }}
                />
              ) : (
                <FormSteps
                  step={step}
                  other={other}
                  setGrecaptcha={setGrecaptcha}
                  setCaptchaToken={setCaptchaToken}
                  contractors={contractors}
                  frameworks={frameworks}
                  choices={choices}
                />
              )}
            </div>
            {notification}
            <div className={styles.rowPrevNext}>
              <Button
                colour={'green'}
                disabled={step === Steps.Route || loading}
                onClick={prev}
                label={'Back'}
                loading={loading}
              />
              {step !== Steps.Route && step !== Steps.Results ? (
                <Button
                  colour={'blue'}
                  onClick={next}
                  label={'Save & Continue'}
                />
              ) : null}
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export { MultiForm, Steps }
export type { WizardRoute, WizardData }
